/**
 * Pure helpers + constants for invoices. Side-effect-free; safe to import
 * anywhere (forms, preview, PDF generator).
 */

/**
 * Parse a date value without accidental timezone rollback.
 *
 * JS's `new Date('2026-08-08')` treats bare YYYY-MM-DD as UTC midnight,
 * which shifts one day earlier in negative-offset zones (Americas). Detect
 * that shape and construct a *local* date instead so the day sticks.
 */
export const parseInvoiceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatInvoiceDate = (dateString) => {
  const date = parseInvoiceDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const calculateInvoiceTotals = (items, taxRate, discount, depositAmount) => {
  const subtotal = (items || []).reduce((sum, item) => sum + (item.amount || 0), 0);
  const tax = (subtotal * (taxRate || 0)) / 100;
  const total = subtotal + tax - (discount || 0);
  const balanceDue = total - (depositAmount || 0);
  return { subtotal, tax, total, balanceDue };
};

/**
 * Generate the next invoice number WITHOUT collisions.
 *
 * The old logic used `invoices.length + 1`, which re-issued an existing number
 * whenever any invoice was deleted (e.g. [001,002,003], delete 002 -> length 2
 * -> next "003" DUPLICATE). This instead scans the highest sequence actually in
 * use for the current year's `PREFIX-YYYY-` series and returns max+1, then
 * guards against any lingering collision (e.g. a manually typed number).
 *
 * @param {Array} invoices existing invoices/estimates (each with invoiceNumber or estimateNumber)
 * @param {string} prefix  "INV" (default) or "EST"
 */
export const getNextDocumentNumber = (invoices = [], prefix = 'INV') => {
  const year = new Date().getFullYear();
  const existing = new Set(
    (invoices || [])
      .map((inv) => (inv?.invoiceNumber || inv?.estimateNumber || '').trim().toUpperCase())
      .filter(Boolean)
  );
  const re = new RegExp(`^${prefix}-${year}-(\\d+)$`, 'i');
  let maxSeq = 0;
  existing.forEach((num) => {
    const m = num.match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n) && n > maxSeq) maxSeq = n;
    }
  });
  let seq = maxSeq + 1;
  let candidate = `${prefix}-${year}-${String(seq).padStart(3, '0')}`;
  while (existing.has(candidate.toUpperCase())) {
    seq += 1;
    candidate = `${prefix}-${year}-${String(seq).padStart(3, '0')}`;
  }
  return candidate;
};

/** True if `number` is already used by any invoice (case-insensitive). */
export const isDocumentNumberTaken = (invoices = [], number = '') => {
  const n = (number || '').trim().toUpperCase();
  if (!n) return false;
  return (invoices || []).some(
    (inv) => (inv?.invoiceNumber || inv?.estimateNumber || '').trim().toUpperCase() === n
  );
};

export const CLEANING_SERVICES = [
  { description: 'Standard House Cleaning', rate: 120 },
  { description: 'Deep Cleaning Service', rate: 200 },
  { description: 'Move-In/Move-Out Cleaning', rate: 250 },
  { description: 'Post-Construction Cleaning', rate: 300 },
  { description: 'Window Cleaning (Interior & Exterior)', rate: 80 },
  { description: 'Carpet Cleaning', rate: 150 },
  { description: 'Kitchen Deep Clean', rate: 100 },
  { description: 'Bathroom Deep Clean', rate: 80 },
  { description: 'Oven & Refrigerator Cleaning', rate: 60 },
  { description: 'Laundry Service', rate: 50 },
  { description: 'Garage Cleaning', rate: 90 },
  { description: 'Basement Cleaning', rate: 110 },
  { description: 'Pressure Washing (Exterior)', rate: 180 },
  { description: 'Upholstery Cleaning', rate: 120 },
  { description: 'Air Duct Cleaning', rate: 200 },
];
